import { practiceCoveragePct, type Escalation, type IntegritySummary } from '@/lib/api/integrity';

/**
 * The verification story as one file a TPO can forward.
 *
 * Self-contained HTML rather than a PDF: it prints to PDF from any browser, it
 * needs no server-side renderer, and it stays readable in ten years when
 * whatever we would have generated no longer opens. The existing Reports tab
 * already builds its CSVs client-side, so this follows the same shape.
 *
 * The audience is not the TPO. It is whoever the TPO forwards it to — a Dean, a
 * director, a committee — none of whom have seen any of our screens. So it
 * repeats the method in full and never assumes a reader who already knows what
 * "arrived whole" means.
 */

export interface ReportInput {
  orgName: string;
  summary: IntegritySummary;
  bands: { label: string; count: number }[];
  /**
   * Included only when the TPO explicitly asked. This file gets forwarded, and
   * a report that carries three students' names by default will one day reach a
   * WhatsApp group; making it a decision somebody took is the only footing on
   * which names should travel.
   */
  escalations?: Escalation[];
}

const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

const row = (label: string, value: string | number) =>
  `<tr><td>${esc(label)}</td><td class="n">${esc(String(value))}</td></tr>`;

export function buildIntegrityReport(input: ReportInput): string {
  const { orgName, summary: s, bands, escalations } = input;
  const pct = Math.round(practiceCoveragePct(s.coverage));
  const generated = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const namesSection =
    escalations && escalations.length > 0
      ? `<h2>Cases raised to the TPO</h2>
    <table>
      <tr><th>Student</th><th>What the trainer found</th></tr>
      ${escalations
        .map(
          (e) =>
            `<tr><td>${esc(e.student_name)}</td><td class="note">${esc(e.note)}<span class="by">— ${esc(e.raised_by)}</span></td></tr>`,
        )
        .join('')}
    </table>`
      : '';

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>${esc(orgName)} — placement readiness &amp; verification</title>
<style>
  @media print { body { margin: 0 } .page { box-shadow: none; margin: 0 } }
  body { background:#f6f7f9; margin:0;
         font:14px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
         color:#0f172a; }
  .page { max-width:70ch; margin:32px auto; background:#fff; padding:40px;
          border:1px solid #e2e8f0; border-radius:10px; box-shadow:0 1px 3px rgba(15,23,42,.06); }
  h1 { font-size:20px; margin:0 0 2px; letter-spacing:-.01em }
  .meta { color:#64748b; font-size:12.5px; margin:0 0 24px }
  h2 { font-size:11.5px; text-transform:uppercase; letter-spacing:.06em; color:#64748b;
       margin:26px 0 8px; padding-bottom:5px; border-bottom:1px solid #f1f5f9 }
  table { border-collapse:collapse; width:100%; font-size:13.5px }
  td, th { text-align:left; padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:top }
  th { font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#64748b }
  td.n { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap }
  .lead { font-size:15px; line-height:1.55; margin:0 0 4px }
  .lead b { font-size:22px; font-variant-numeric:tabular-nums }
  .cant { font-size:12.5px; color:#64748b; margin:8px 0 0 }
  .method { font-size:12.5px; color:#475569; line-height:1.65 }
  .note .by { display:block; font-size:11px; color:#64748b; margin-top:2px }
  .foot { margin-top:28px; padding-top:14px; border-top:1px solid #f1f5f9;
          font-size:11.5px; color:#94a3b8 }
</style></head>
<body><div class="page">

<h1>Placement readiness &amp; verification</h1>
<p class="meta">${esc(orgName)} · generated ${esc(generated)}</p>

<h2>Readiness</h2>
<table>
  <tr><th>Band</th><th style="text-align:right">Students</th></tr>
  ${bands.map((b) => row(b.label, b.count)).join('')}
</table>

<h2>What these numbers are built on</h2>
<p class="lead"><b>${pct}%</b> of practice work has a recorded process —
   ${s.coverage.practice_recorded.toLocaleString()} of
   ${s.coverage.practice_attempts.toLocaleString()} submissions.
   ${s.coverage.repos_read} of ${s.coverage.repos_expected} assignment repositories could be read.</p>
<p class="cant"><b>What we cannot see:</b> work done before this was switched on, all quizzes,
   private repositories, and hand-ins that were not GitHub links. Work with no record is reported
   as having no record — never as having passed.</p>

<h2>What the record shows</h2>
<table>
  <tr><th>Practice</th><th style="text-align:right">Submissions</th></tr>
  ${row('Written here', s.composition.written_here)}
  ${row('Part pasted', s.composition.part_pasted)}
  ${row('Arrived whole', s.composition.arrived_whole)}
  <tr><th>Assignment repositories</th><th style="text-align:right">Hand-ins</th></tr>
  ${row('Built over time', s.composition.built_over_time)}
  ${row('Done in one sitting', s.composition.one_sitting)}
  ${row('Arrived in one commit', s.composition.one_commit)}
  <tr><th>Similarity</th><th style="text-align:right">Students</th></tr>
  ${row('Share distinctive code with a classmate', s.composition.similar_groups)}
</table>

<h2>What was done about it — last ${s.since_days} days</h2>
<table>
  ${row('Concerns raised', s.handling.raised)}
  ${row('Resolved by a trainer as nothing', s.handling.resolved)}
  ${row('Raised to the TPO', s.handling.escalated)}
  ${row('Still waiting on the TPO', s.handling.unresolved)}
</table>
<p class="cant">Most flags turn out to be nothing. That is the system working — a signal that were
   always right would be one nobody needed a person for.</p>

${namesSection}

<h2>Method</h2>
<p class="method">
  Practice work is measured by what the editor recorded while it was written: how much was typed and
  how much arrived in a single paste. Assignment repositories are measured by their public commit
  history — how many commits, over how long, and by whom. Similar code is found by comparing
  structure, ignoring names, comments and formatting, so reindented or renamed code still matches.
  <br><br>
  None of these prove anything on their own. Every one is a prompt for a trainer to ask a question,
  and most turn out to be nothing. A student who pasted an earlier attempt of their own, or shared a
  lab bench with a classmate, produces the same signal as one who did not do the work; only the
  conversation tells them apart, and that conversation is a person's job, not the software's.
</p>

<p class="foot">Generated by KodPrep. Figures are point-in-time except where a window is stated.</p>
</div></body></html>`;
}

/** Saves the report. Naming includes the date because these get filed. */
export function downloadIntegrityReport(input: ReportInput) {
  const html = buildIntegrityReport(input);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${input.orgName.replace(/\s+/g, '-').toLowerCase()}-readiness-${new Date().toISOString().slice(0, 10)}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
